import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';

import { CustomerUserDTO } from '../../models/customer.model';

type MenuActionId = 'viewProfile' | 'manageRoles' | 'removeUser';

interface MenuAction {
  id: MenuActionId;
  icon: string;
  labelKey: string;
  destructive: boolean;
  disabled: boolean;
}

const NORMAL_ACTIONS: readonly MenuAction[] = [
  {
    id: 'viewProfile',
    icon: 'person',
    labelKey: 'customerDetail.actions.viewProfile',
    destructive: false,
    disabled: false,
  },
  {
    id: 'manageRoles',
    icon: 'shield',
    labelKey: 'customerDetail.actions.permissions',
    destructive: false,
    disabled: false,
  },
];

const REMOVE_ACTION: Omit<MenuAction, 'labelKey'> = {
  id: 'removeUser',
  icon: 'delete_outline',
  destructive: true,
  disabled: false,
};

@Component({
  selector: 'app-user-row-actions-menu',
  imports: [TranslatePipe, MatIcon, MatTooltip, CdkOverlayOrigin, CdkConnectedOverlay],
  templateUrl: './user-row-actions-menu.component.html',
  styleUrl: './user-row-actions-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserRowActionsMenuComponent {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  readonly user = input.required<CustomerUserDTO>();
  readonly canDelete = input<boolean>(false);
  /** Einladungs-Zeilen entfernen keinen Benutzer, sondern widerrufen die Einladung. */
  readonly removeLabelKey = input<string>('customerDetail.actions.removeUser');

  readonly viewProfile = output<CustomerUserDTO>();
  readonly manageRoles = output<CustomerUserDTO>();
  readonly removeUser = output<CustomerUserDTO>();

  readonly open = signal(false);
  readonly activeIndex = signal(0);

  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLButtonElement>;

  readonly items = computed<readonly MenuAction[]>(() =>
    this.canDelete() ? [...NORMAL_ACTIONS, { ...REMOVE_ACTION, labelKey: this.removeLabelKey() }] : NORMAL_ACTIONS,
  );

  readonly destructiveIndex = computed(() => this.items().findIndex((i) => i.destructive));

  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
  ];

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    if (this.open()) {
      this.close();
    } else {
      this.openMenu(0);
    }
  }

  openMenu(focusIndex: number): void {
    this.activeIndex.set(focusIndex);
    this.open.set(true);
    queueMicrotask(() => this.focusItem(focusIndex));
  }

  close(returnFocusToTrigger = false): void {
    if (!this.open()) return;
    this.open.set(false);
    if (returnFocusToTrigger) {
      this.triggerRef?.nativeElement.focus();
    }
  }

  onItemClick(item: MenuAction): void {
    if (item.disabled) return;
    this.emit(item.id);
    this.close();
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openMenu(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.openMenu(this.items().length - 1);
    }
  }

  onMenuKeydown(event: KeyboardEvent): void {
    const itemsLen = this.items().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive((this.activeIndex() + 1) % itemsLen);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive((this.activeIndex() - 1 + itemsLen) % itemsLen);
        break;
      case 'Home':
        event.preventDefault();
        this.moveActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.moveActive(itemsLen - 1);
        break;
      case 'Escape':
        event.preventDefault();
        this.close(true);
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  private moveActive(index: number): void {
    this.activeIndex.set(index);
    this.focusItem(index);
  }

  private focusItem(index: number): void {
    const root: Document | HTMLElement = this.host.nativeElement.ownerDocument ?? document;
    const items = root.querySelectorAll<HTMLButtonElement>('.uram__menu [role="menuitem"]');
    items.item(index)?.focus();
  }

  private emit(id: MenuActionId): void {
    const u = this.user();
    if (id === 'viewProfile') this.viewProfile.emit(u);
    else if (id === 'manageRoles') this.manageRoles.emit(u);
    else if (id === 'removeUser') this.removeUser.emit(u);
  }
}
